import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import { BIZ } from "@/constants/content";
import { EASE } from "@/components/Reveal";

const LINKS = [
  { href: "#services", label: "Services" },
  { href: "#van", label: "Mobile Van" },
  { href: "/nationwide-dental-handpiece-repair", label: "Nationwide" },
  { href: "#pricing", label: "Pricing" },
  { href: "#story", label: "Our Story" },
  { href: "#faq", label: "FAQ" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "border-b border-ink/10 bg-white/85 shadow-[0_8px_30px_rgba(22,50,79,0.06)] backdrop-blur-xl" : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <a href="#top" className="shrink-0" data-testid="nav-logo-link" aria-label="JSL Dental Repair — back to top">
          <Logo />
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="link-underline text-[13.5px] font-semibold text-ink/80 hover:text-ink"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={BIZ.jacob.tel}
            data-testid="call-jacob-button"
            className="btn-lift inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-4 py-2.5 text-[13px] font-bold text-ink hover:border-ink/40"
          >
            <Phone className="h-3.5 w-3.5 text-brandsky-deep" />
            {BIZ.jacob.phone}
          </a>
          <a
            href="#intake"
            data-testid="nav-intake-button"
            className="btn-lift group inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-[13px] font-bold text-white shadow-[0_10px_24px_rgba(22,50,79,0.28)] hover:bg-abyss"
          >
            Send an intake
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </a>
        </div>

        <button
          className="rounded-xl border border-ink/15 bg-white/80 p-2.5 lg:hidden"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-button"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="overflow-hidden border-b border-ink/10 bg-white/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu-panel"
          >
            <div className="space-y-1 px-4 py-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="block rounded-xl px-4 py-3 text-[15px] font-semibold text-ink hover:bg-cloud"
                  data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </a>
              ))}
              <div className="grid grid-cols-2 gap-2 pt-3">
                <a
                  href={BIZ.jacob.tel}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-ink/15 px-3 py-3 text-[13px] font-bold"
                  data-testid="mobile-call-jacob-button"
                >
                  <Phone className="h-3.5 w-3.5 text-brandsky-deep" /> Jacob
                </a>
                <a
                  href={BIZ.jonah.tel}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-ink/15 px-3 py-3 text-[13px] font-bold"
                  data-testid="mobile-call-jonah-button"
                >
                  <Phone className="h-3.5 w-3.5 text-brandsky-deep" /> Jonah
                </a>
              </div>
              <a
                href="#intake"
                onClick={() => setOpen(false)}
                className="mt-2 flex items-center justify-center gap-2 rounded-xl bg-ink px-4 py-3.5 text-[14px] font-bold text-white"
                data-testid="mobile-intake-button"
              >
                Send an intake <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}